<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: ParentSection                                         |
| Section: components/Person                                       |
| Purpose: Parent relationship section with optional tree fallback.|
+------------------------------------------------------------------+
-->
<template>
    <div class="PersonCardGroup_container"
        v-if="personID && (isLoadingParents || hasAncestors || localParents.length || localDefaultParents.length)">
        <div class="PersonCardGroup_wrapper wrapper">
            <TitleGroup :label="parentTitle" :title="$t('parentSection.sectionTitle')" :isWhiteText="true"
                :showLink="false" />
            <div v-if="isLoadingParents" class="ParentSection_loading">
                {{ $t('treeViews.loadingAncestors') }}
            </div>

            <div class="PersonCardGroup" id="PersonCardGroup_parents" v-else-if="!hasAncestors">
                <PersonCard v-for="parent in displayParents" :key="parent.personID || parent.nodeID" :photoSrc="parent.photoSrc"
                    :name="parent.name" :years="parent.years" :gender="parent.gender" :href="parent.href"
                    :isSmall="parent.isSmall" :isDefault="parent.isDefault"
                    :personID="parent.personID" :firstname="parent.firstname" :lastname="parent.lastname"
                    :living="parent.living"
                    :placeholder="parent.placeholder"
                    :class="{ 'PersonCard_small_guest': isPrivatePerson(parent) }" />
            </div>

            <!-- FamilyTree kept for reference (non-destructively disabled) -->
            <!-- <FamilyTree v-if="personID && hasAncestors" :personID="personID" :generations="3" /> -->

            <ParentTreePanel v-if="personID && hasAncestors" :personId="personID" :includeOriginSiblings="true" />

            <CollapsiblePanel v-if="citations.length > 0" :label="$t('parentSection.annotations')">
                <!-- Display Citations if any exist -->
                <ul class="collapsible_list">
                    <li v-for="(citation, index) in citations" :key="'citation-' + index">
                        <p>{{ citation.title }}</p>
                    </li>
                </ul>
            </CollapsiblePanel>

            <img class="decoration decoration_top_right--offset" src="@/assets/decorations/merapi.svg">
        </div>
    </div>
    <!-- <div class="ImageDivider_container" ref="imageDivider" v-if="shouldShowParentLink">
        <div class="ImageDivider_wrapper wrapper">
            <div class="TitleBtnGroup">
                <h2>Duik dieper in de familiegeschiedenis</h2>
                <a :href="`/person/${personID}/ancestors`" class="BtnPrimary">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="#FFF">
                        <path
                            d="M15.7063 9.20627C16.0969 8.81565 16.0969 8.18127 15.7063 7.79065L11.7063 3.79065C11.3156 3.40002 10.6812 3.40002 10.2906 3.79065C9.9 4.18127 9.9 4.81565 10.2906 5.20627L12.5844 7.50002H1C0.446875 7.50002 0 7.9469 0 8.50002C0 9.05315 0.446875 9.50002 1 9.50002H12.5844L10.2906 11.7938C9.9 12.1844 9.9 12.8188 10.2906 13.2094C10.6812 13.6 11.3156 13.6 11.7063 13.2094L15.7063 9.2094V9.20627Z"
                            fill="#FFF" />
                    </svg>
                    Bekijk alle voorouders
                </a>
            </div>
        </div>
    </div> -->
</template>

<script>
// import BtnPrimary from '@/atoms/BtnPrimary.vue';
import TitleGroup from '@/atoms/TitleGroup.vue';
import PersonCard from '@/atoms/PersonCard.vue';
import CollapsiblePanel from '@/components/CollapsiblePanel.vue';
// import FamilyTree from '@/views/trees/FamilyTree.vue'; // kept for reference
import ParentTreePanel from '@/components/Person/ParentTreePanel.vue';
import { formatYears } from '@/utils/dateUtils.js'; // Import the utility function
import { getGenderBasedText } from '@/utils/textUtils';
import { getAncestors, getParents } from '@/api/personService.js'; // Import the required service functions
import { getCitations } from '@/api/personService.js';
import { getDefaultPhoto, getPhotoSrcWithFallback } from '@/utils/photoUtils'; // Import the utility function
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { isLivingPlaceholder, isPrivateLivingPerson } from '@/utils/personPrivacy';

export default {
    components: {
        // BtnPrimary,
        TitleGroup,
        PersonCard,
        CollapsiblePanel,
        // FamilyTree,
        ParentTreePanel
    },
    props: {
        shouldShowLink: Boolean,
        dynamicLinkLabel: String,
        personID: String, // Pass personID as a prop to fetch parent data
        person: Object // Add a prop for person data
    },
    setup() {
        // Injecting properties from App.vue using the useAuth composable
        const { authToken, username, logout } = useAuth();
        const { localePath } = useLocale();

        return {
            authToken,
            username,
            logout,
            localePath,
        };
    },
    data() {
        return {
            defaultParents: [
                {
                    personID: 'default1',
                    name: '',
                    years: 'Onbekend',
                    gender: 'male',
                    href: 'javascript:void(0)',
                    isSmall: false,
                    isDefault: true
                },
                {
                    personID: 'default2',
                    name: '',
                    years: 'Onbekend',
                    gender: 'female',
                    href: 'javascript:void(0)',
                    isSmall: false,
                    isDefault: true
                }
            ],
            defaultParentMessages: [],
            localDefaultParents: [], // Local copy to avoid mutating props
            localParents: [], // Local copy of parents to avoid mutating props
            citations: [], // To store citations dynamically
            isLoadingParents: false,
            ancestorTreeAvailable: false,
            parentRequestId: 0
        };
    },
    watch: {
        personID: {
            immediate: true,
            handler(personID) {
                this.loadParentSection(personID);
            }
        }
    },
    methods: {
        resetParentState() {
            this.localDefaultParents = JSON.parse(JSON.stringify(this.defaultParents));
            this.localParents = [];
            this.citations = [];
            this.ancestorTreeAvailable = false;
        },
        async loadParentSection(personID) {
            const requestId = this.parentRequestId + 1;
            this.parentRequestId = requestId;
            this.resetParentState();

            if (!personID) {
                this.isLoadingParents = false;
                return;
            }

            this.isLoadingParents = true;

            try {
                await Promise.all([
                    this.fetchParentsWithPhotos(personID, requestId),
                    this.fetchCitations(personID, requestId)
                ]);
            } finally {
                if (requestId === this.parentRequestId) {
                    this.isLoadingParents = false;
                }
            }
        },
        isPrivatePerson(person) {
            return isPrivateLivingPerson(person, this.authToken);
        },
        async fetchCitations(personID, requestId = this.parentRequestId) {
            try {
                const citations = await getCitations(personID);
                if (requestId !== this.parentRequestId) {
                    return;
                }
                console.log('Fetched Citations:', citations); // Debugging
                this.citations = citations || []; // Assign fetched citations
            } catch (error) {
                if (requestId !== this.parentRequestId) {
                    return;
                }
                console.error(`Error fetching citations for personID ${personID}:`, error);
                this.citations = []; // Default to an empty array if fetching fails
            }
        },
        createDefaultParent(gender) {
            return {
                personID: `default-${gender}`,
                name: this.getRandomParentMessage(),
                photoSrc: getDefaultPhoto(gender), // Use getDefaultPhoto function for default image
                years: this.$t('parentSection.unknownYears'),
                gender: gender,
                href: 'javascript:void(0)',
                isSmall: false,
                isDefault: true
            };
        },
        async fetchParentsWithPhotos(personID, requestId = this.parentRequestId) {
            try {
                const fetchedParents = await getParents(personID);
                if (requestId !== this.parentRequestId) {
                    return;
                }
                console.log('Fetched Parents:', fetchedParents); // Debugging: Check fetched parents

                const realParents = Array.isArray(fetchedParents)
                    ? fetchedParents.filter(parent => this.isRealParent(parent))
                    : [];

                if (realParents.length > 0) {
                    // Map through fetched parents and set photoSrc based on provided photoPath or gender default
                    const parentsWithPhotos = realParents.map(parent => ({
                        ...parent,
                        photoSrc: parent.photoPath || getDefaultPhoto(parent.sex) // Use photoPath or fallback
                    }));
                    this.processParents(parentsWithPhotos); // Handle fetched parents
                } else {
                    await this.resolveParentFallback(personID, requestId);
                }
            } catch (error) {
                if (requestId !== this.parentRequestId) {
                    return;
                }
                console.error('Error fetching parents data:', error);
                await this.resolveParentFallback(personID, requestId);
            }
        },
        async resolveParentFallback(personID, requestId) {
            const hasAncestorTreeData = await this.hasAncestorTreeData(personID);

            if (requestId !== this.parentRequestId) {
                return;
            }

            if (hasAncestorTreeData) {
                this.ancestorTreeAvailable = true;
                return;
            }

            this.assignRandomNamesToDefaultParents(); // Use default parents if fetching fails
        },
        async hasAncestorTreeData(personID) {
            try {
                const ancestorData = await getAncestors(personID, 1);
                const ancestors = Array.isArray(ancestorData?.ancestors)
                    ? ancestorData.ancestors
                    : [];

                return ancestors.some(parent => this.isRealParent(parent));
            } catch (error) {
                console.error('Error checking ancestor tree fallback:', error);
                return false;
            }
        },
        processParents(fetchedParents) {
            const parents = [];

            // Determine if father and/or mother are present
            const realParents = fetchedParents.filter(parent => this.isRealParent(parent));
            const father = realParents.find(parent => parent.sex === 'M' || parent.parentRole === 'father');
            const mother = realParents.find(parent => parent.sex === 'F' || parent.parentRole === 'mother');

            if (!father) {
                parents.push(this.createDefaultParent('male')); // Add default father card first if missing
            }
            if (father) {
                parents.push(this.mapParent(father)); // Add actual father card if present
            }
            if (mother) {
                parents.push(this.mapParent(mother)); // Add actual mother card if present
            }
            if (!mother) {
                parents.push(this.createDefaultParent('female')); // Add default mother card second if missing
            }

            this.localParents = parents; // Assign the ordered parents array to local state
        },
        isRealParent(parent) {
            if (isLivingPlaceholder(parent)) return Boolean(parent.nodeID);
            const parentID = String(parent?.personID || '').trim();
            return !!parentID && parentID !== this.personID && !parent?.isDefault && !parentID.startsWith('default');
        },
        mapParent(parent) {
            if (isLivingPlaceholder(parent)) {
                return {
                    ...parent,
                    name: this.$t('privacy.livingPlaceholder'),
                    gender: 'unknown',
                    years: '',
                    photoSrc: '',
                    href: 'javascript:void(0)',
                    isSmall: false,
                    isDefault: false,
                };
            }
            return {
                personID: parent.personID,
                name: parent.name,
                gender: parent.sex === 'M' ? 'male' : parent.sex === 'F' ? 'female' : 'unknown',
                years: formatYears(parent.birthdate, parent.altbirthdate, parent.deathdate, parent.burialdate, parent.living),
                photoSrc: parent.photoSrc,
                href: this.localePath(`/person/${parent.personID}`),
                isSmall: false,
                isDefault: false,
                living: parent.living // Add this line
            };
        },
        getRandomParentMessage() {
            const messages = this.$tm('parentSection.defaultMessages');
            if (!messages || !messages.length) return '';
            const raw = messages[Math.floor(Math.random() * messages.length)];
            return typeof raw === 'function' ? raw() : String(raw);
        },
        assignRandomNamesToDefaultParents() {
            const messages = this.$tm('parentSection.defaultMessages');
            const toStr = raw => (typeof raw === 'function' ? raw() : String(raw));
            const pool = messages && messages.length ? messages.map(toStr) : ['', ''];

            const firstIndex = Math.floor(Math.random() * pool.length);
            const firstMessage = pool[firstIndex];
            const remaining = pool.filter((_, i) => i !== firstIndex);
            const secondMessage = remaining[Math.floor(Math.random() * remaining.length)] || firstMessage;

            this.localDefaultParents = this.localDefaultParents.map((parent, index) => ({
                ...parent,
                name: index === 0 ? firstMessage : secondMessage,
            }));
        },
    },
    computed: {
        // True if there is at least one real parent (not default)
        hasAncestors() {
            return this.hasRealParents || this.ancestorTreeAvailable;
        },
        displayParents() {
            return (this.localParents.length ? this.localParents : this.localDefaultParents).map(parent => ({
                ...parent,
                photoSrc: this.isPrivatePerson(parent) ? '' : getPhotoSrcWithFallback({
                    photoSrc: parent.photoSrc,
                    photoPath: parent.photoPath,
                    sex: parent.sex,
                    gender: parent.gender
                }),
                name: this.isPrivatePerson(parent) ? this.$t('searchResults.privacyHidden') : parent.name,
                years: this.isPrivatePerson(parent) ? '' : (parent.isDefault ? this.$t('parentSection.unknownYears') : parent.years),
                href: parent.href,
                isDefault: parent.isDefault,
                isSmall: parent.isSmall,
                gender: parent.gender
            }));
        },
        parentTitle() {
            return getGenderBasedText('ꦮꦺꦴꦁ​ꦠꦸꦮ', this.person);
        },
        hasRealParents() {
            return this.localParents.length > 0 && this.localParents.some(parent => this.isRealParent(parent));
        }
    }
};
</script>

<style scoped>
.TitleBtnGroup {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
    background-color: var(--brand-bg-1);
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-card-sm);
    padding: 96px;
}

.NoteGroup_wrapper h3 {
    margin-bottom: 12px;
}

.collapsible_list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
}

.ParentSection_loading {
    color: rgba(255, 255, 255, 0.82);
    font-size: 14px;
    font-weight: 600;
    padding: 48px 0;
    text-align: center;
}

.ImageDivider_container {
    background: url('@/assets/noise128x128.png'), linear-gradient(to bottom, var(--brand-bg-3) 50%, var(--brand-bg-2) 50%);
    background-repeat: repeat;
    background-size: auto;
    transition: var(--transition-bounce);
}

.ImageDivider_wrapper {
    padding-left: 20px;
    padding-right: 20px;
}

#parentSection>.wrapper {
    max-width: none !important;
}
</style>
